import { FC, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Market, Order } from "@module-bindings/types";
import { assetIdKey, formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";
import type { AssetId } from "@module-bindings/types";

type ViewMode = "both" | "bids" | "asks";

const FRAC_BITS = 60n;

/** Convert fixed-point u128 price to a display number. */
function priceToNumber(raw: bigint): number {
  const integer = Number(raw >> FRAC_BITS);
  const fractional = Number(raw & ((1n << FRAC_BITS) - 1n)) / Number(1n << FRAC_BITS);
  return integer + fractional;
}

interface PriceLevel {
  price: number;
  amount: number; // aggregated remaining at this level
  total: number;  // cumulative
}

/** Aggregate orders into price levels, sorted best-first. */
function buildLevels(orders: readonly Order[], side: "Bid" | "Ask"): PriceLevel[] {
  // Group by price
  const byPrice = new Map<number, number>();
  for (const o of orders) {
    if (o.side.tag !== side || o.remaining === 0n) continue;
    const p = priceToNumber(o.price);
    byPrice.set(p, (byPrice.get(p) ?? 0) + Number(o.remaining));
  }

  // Sort: bids high→low, asks low→high
  const sorted = [...byPrice.entries()].sort((a, b) =>
    side === "Bid" ? b[0] - a[0] : a[0] - b[0]
  );

  let cumulative = 0;
  return sorted.map(([price, amount]) => {
    cumulative += amount;
    return { price, amount, total: cumulative };
  });
}

interface OrderBookProps {
  selectedMarket: Market | null;
}

export const OrderBook: FC<OrderBookProps> = ({ selectedMarket }) => {
  const [viewMode, setViewMode] = useState<ViewMode>("both");
  const [allOrders] = useTable(tables.orders);

  // Filter orders to the selected market
  const marketOrders = useMemo(() => {
    if (!selectedMarket) return [];
    const baseKey = assetIdKey(selectedMarket.tradingPair.base);
    const quoteKey = assetIdKey(selectedMarket.tradingPair.quote);
    return allOrders.filter((o) => {
      const ob = assetIdKey(o.pair.base);
      const oq = assetIdKey(o.pair.quote);
      return ob === baseKey && oq === quoteKey;
    });
  }, [allOrders, selectedMarket]);

  const askLevels = useMemo(() => buildLevels(marketOrders, "Ask"), [marketOrders]);
  const bidLevels = useMemo(() => buildLevels(marketOrders, "Bid"), [marketOrders]);

  const baseLabel = selectedMarket ? formatAssetLabel(selectedMarket.tradingPair.base) : "Base";
  const quoteLabel = selectedMarket ? formatAssetLabel(selectedMarket.tradingPair.quote) : "Quote";

  const showAsks = viewMode !== "bids";
  const showBids = viewMode !== "asks";
  const showSpread = viewMode === "both";

  // Spread
  const bestAsk = askLevels[0]?.price;
  const bestBid = bidLevels[0]?.price;
  const spread = bestAsk != null && bestBid != null ? bestAsk - bestBid : null;
  const spreadPct = spread != null && bestAsk > 0
    ? ((spread / bestAsk) * 100).toFixed(2)
    : null;

  const maxAskTotal = askLevels.length > 0 ? askLevels[askLevels.length - 1].total : 1;
  const maxBidTotal = bidLevels.length > 0 ? bidLevels[bidLevels.length - 1].total : 1;

  // For "both" mode, show asks reversed so best ask is at the bottom (near spread)
  const displayAsks = showSpread ? [...askLevels].reverse() : askLevels;

  return (
    <div className="rounded-xl bg-[#23141A] flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <h3 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
          Order Book
        </h3>
        <div className="flex items-center gap-0.5">
          <button onClick={() => setViewMode("both")} className="p-1 rounded" title="Both">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
            </svg>
          </button>
          <button onClick={() => setViewMode("bids")} className="p-1 rounded" title="Bids">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
            </svg>
          </button>
          <button onClick={() => setViewMode("asks")} className="p-1 rounded" title="Asks">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
            </svg>
          </button>
        </div>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-3 items-end text-[9px] text-sr-neutral-500 uppercase tracking-wider px-2 pb-1">
        <span className="px-2">Price</span>
        <span className="text-right">Amount</span>
        <span className="text-right px-2">Total</span>
      </div>

      {/* Asks */}
      {showAsks && (
        <div
          className={`flex flex-col overflow-hidden ${
            showSpread ? "flex-1 justify-end" : "flex-1 pb-5"
          }`}
        >
          {displayAsks.length > 0 ? displayAsks.map((level, i) => (
            <Row key={`ask-${i}`} level={level} side="ask" maxTotal={maxAskTotal} amountAsset={selectedMarket!.tradingPair.base} />
          )) : (
            <div className="flex-1 flex items-end justify-center pb-2">
              <span className="text-xs text-sr-neutral-700">No asks</span>
            </div>
          )}
        </div>
      )}

      {/* Spread */}
      {showSpread && (
        <div className="flex h-7 items-center justify-between border-y border-sr-border-subtle/50 px-3 text-[10px]">
          <span className="text-sr-neutral-500">Spread</span>
          {spread != null ? (
            <div className="flex items-center gap-2">
              <span className="text-sr-neutral-300 tabular-nums">{spreadPct}%</span>
              <span className="text-white tabular-nums font-display">{spread.toFixed(6)}</span>
            </div>
          ) : (
            <span className="text-sr-neutral-700">—</span>
          )}
        </div>
      )}

      {/* Bids */}
      {showBids && (
        <div className="flex flex-1 flex-col overflow-hidden pb-5">
          {bidLevels.length > 0 ? bidLevels.map((level, i) => (
            <Row key={`bid-${i}`} level={level} side="bid" maxTotal={maxBidTotal} amountAsset={selectedMarket!.tradingPair.quote} />
          )) : (
            <div className="flex-1 flex items-start justify-center pt-2">
              <span className="text-xs text-sr-neutral-700">No bids</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Row: FC<{
  level: PriceLevel;
  side: "ask" | "bid";
  maxTotal: number;
  amountAsset: AssetId;
}> = ({ level, side, maxTotal, amountAsset }) => {
  const depthPct = maxTotal > 0 ? (level.total / maxTotal) * 100 : 0;
  const isAsk = side === "ask";
  return (
    <div className="relative grid h-7 shrink-0 grid-cols-3 items-center px-2 text-[10px] hover:bg-white/5 cursor-pointer">
      <div
        className={`absolute inset-y-0 right-0 pointer-events-none ${isAsk ? "bg-sr-salmon/20" : "bg-sr-mint/15"}`}
        style={{ width: `${depthPct}%` }}
      />
      <span className={`relative tabular-nums font-display px-1 ${isAsk ? "text-sr-salmon" : "text-sr-mint"}`}>
        {level.price.toFixed(6)}
      </span>
      <span className="relative text-right text-white tabular-nums">
        {formatAssetValue(amountAsset, BigInt(Math.round(level.amount)))}
      </span>
      <span className="relative text-right text-white tabular-nums px-1">
        {formatAssetValue(amountAsset, BigInt(Math.round(level.total)))}
      </span>
    </div>
  );
};
